import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { RotateCcw, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPatch, apiPost, apiGetAll } from '../../lib/api';

const STATUS_PILL = {
  open: 'text-rose-700 bg-rose-50', acknowledged: 'text-amber-700 bg-amber-50', resolved: 'text-emerald-700 bg-emerald-50',
};
const REQUEST_TYPES = [
  ['replacement', 'Replacement of rejected / damaged material'],
  ['short_supply', 'Short supply against PO'],
  ['quality', 'Quality / grade deviation'],
  ['invoice_correction', 'Invoice correction'],
  ['delivery_delay', 'Delivery delay follow-up'],
  ['other', 'Other'],
];

/**
 * VendorChangeRequests — one list, two roles:
 *  • Stores (`canRaise`)   : raise a request to the vendor against a PO / GRN and track its status.
 *  • Procurement (`canExecute`): acknowledge / resolve with the vendor.
 */
export function VendorChangeRequests({ projectId = '', canRaise = false, canExecute = false, title = 'Vendor Change Requests', subtitle }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [showRaise, setShowRaise] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/vendor-change-requests', { project_id: projectId || undefined, status: statusFilter || undefined });
      setRows(data.items || []);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [projectId, statusFilter]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const updateStatus = async (vcr, status) => {
    try {
      await apiPatch(`/vendor-change-requests/${vcr.id}`, { status });
      toast.success(`Marked ${status}`);
      fetchRows();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-4" data-testid="vcr-list">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold">{title}</h2>
          <p className="text-sm text-gray-500">{subtitle || (canExecute
            ? 'Requests raised by Stores (manually or from rejected GRN inspections). Acknowledge, chase the vendor, then resolve.'
            : 'Raise material / invoice change requests to the vendor. Procurement acknowledges and resolves them.')}</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="vcr-status-filter"
            className="px-3 py-1.5 border border-slate-300 rounded text-sm">
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
          </select>
          {canRaise && (
            <Button onClick={() => setShowRaise(v => !v)} className="bg-[#3b4872] hover:bg-[#2d3856]" data-testid="vcr-raise-btn">
              <Plus className="w-4 h-4 mr-1" /> Raise request
            </Button>
          )}
        </div>
      </div>
      {showRaise && <RaiseVCRForm projectId={projectId} onClose={() => setShowRaise(false)} onCreated={() => { setShowRaise(false); fetchRows(); }} />}
      <Card><CardContent className="p-0"><div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
        <table className="w-full text-sm sticky-col-header">
          <thead className="bg-gray-50 border-b"><tr>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Request</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Vendor</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">PO / GRN</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Items / Remarks</th>
            <th className="text-center px-4 py-3 font-medium text-gray-600">Type</th>
            <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
            {canExecute && <th className="text-center px-4 py-3 font-medium text-gray-600">Action</th>}
          </tr></thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={canExecute ? 7 : 6} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={canExecute ? 7 : 6} className="text-center py-8 text-gray-500">No vendor change requests</td></tr>
            ) : rows.map(v => (
              <tr key={v.id} className="hover:bg-gray-50 align-top" data-testid={`vcr-row-${v.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{v.vcr_no}<div className="text-[10px] text-slate-400 font-sans">{v.created_by} · {(v.created_at || '').slice(0, 10)}</div></td>
                <td className="px-4 py-3">{v.vendor_name || '—'}</td>
                <td className="px-4 py-3 font-mono text-xs">{v.po_no || '—'}{v.grn_no ? <div className="text-slate-400">{v.grn_no}</div> : null}</td>
                <td className="px-4 py-3 text-xs">
                  {(v.items || []).map((it, i) => (
                    <div key={i}>{it.description} — <span className="text-rose-700">{it.rejected_qty} {it.unit}</span>{it.reasons?.length ? <span className="text-gray-400"> ({it.reasons.join(', ')})</span> : null}</div>
                  ))}
                  {v.remarks && <div className="text-slate-500 italic">{v.remarks}</div>}
                </td>
                <td className="px-4 py-3 text-center text-xs">
                  {v.request_type && <span className="block text-[10px] uppercase tracking-wider text-slate-500">{v.request_type.replace(/_/g, ' ')}</span>}
                  {v.replacement_requested && <span className="inline-flex items-center gap-1 text-[10px] text-slate-600"><RotateCcw className="w-3 h-3" /> Replace</span>}
                  {v.new_invoice_expected && <span className="block text-[10px] text-slate-600">New invoice</span>}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_PILL[v.status] || ''}`} data-testid={`vcr-status-${v.id}`}>{v.status}</span>
                  {v.status_note && <div className="text-[10px] text-slate-400">{v.status_note}</div>}
                </td>
                {canExecute && (
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    {v.status === 'open' && <Button variant="ghost" size="sm" data-testid={`vcr-ack-${v.id}`} onClick={() => updateStatus(v, 'acknowledged')}>Acknowledge</Button>}
                    {v.status !== 'resolved' && <Button variant="ghost" size="sm" data-testid={`vcr-resolve-${v.id}`} onClick={() => updateStatus(v, 'resolved')}>Resolve</Button>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div></CardContent></Card>
    </div>
  );
}

function RaiseVCRForm({ projectId, onClose, onCreated }) {
  const [pos, setPos] = useState([]);
  const [poId, setPoId] = useState('');
  const [type, setType] = useState('replacement');
  const [lines, setLines] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [newInvoice, setNewInvoice] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGetAll('/po', projectId ? { project_id: projectId } : {}).then(r => setPos(r.items || [])).catch(() => {});
  }, [projectId]);
  const po = pos.find(p => p.id === poId);
  const poLines = po ? (po.items || po.line_items || po.boq_items || []) : [];
  const toggleLine = (ln) => {
    const key = ln.boq_id || ln.material_id || ln.description;
    setLines(cur => cur.some(l => l.key === key) ? cur.filter(l => l.key !== key)
      : [...cur, { key, boq_id: ln.boq_id, material_id: ln.material_id, description: ln.description || ln.material_name, unit: ln.unit || ln.uom, rejected_qty: 0, reasons: [] }]);
  };
  const setQty = (key, q) => setLines(cur => cur.map(l => l.key === key ? { ...l, rejected_qty: q } : l));

  const submit = async () => {
    if (!poId) return toast.error('Select the PO');
    if (!lines.length && !remarks) return toast.error('Pick at least one line or write a remark');
    setSaving(true);
    try {
      const r = await apiPost('/vendor-change-requests', {
        po_id: poId, project_id: projectId || undefined, request_type: type, remarks: remarks || undefined,
        replacement_requested: ['replacement', 'short_supply', 'quality'].includes(type), new_invoice_expected: newInvoice,
        items: lines.map(({ key, ...l }) => ({ ...l, rejected_qty: Number(l.rejected_qty) || 0 })),
      });
      toast.success(`${r.vcr_no} raised — Procurement notified`);
      onCreated?.(r);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not raise request'); }
    finally { setSaving(false); }
  };

  const field = 'border border-slate-300 rounded-sm px-3 py-2 text-sm w-full bg-white focus:outline-none focus:border-[#3b4872]';
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="vcr-raise-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-slate-800">Raise a request to the vendor</h3>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="vcr-raise-cancel"><X size={16} /></button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-xs text-slate-600">Purchase Order
          <select value={poId} onChange={e => { setPoId(e.target.value); setLines([]); }} className={field} data-testid="vcr-po-select">
            <option value="">Select PO…</option>
            {pos.map(p => <option key={p.id} value={p.id}>{p.po_no} — {p.vendor_name || p.vendor?.name || ''}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-600">Request type
          <select value={type} onChange={e => setType(e.target.value)} className={field} data-testid="vcr-type-select">
            {REQUEST_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      {po && (
        <div className="border rounded-md divide-y max-h-56 overflow-y-auto">
          {poLines.length === 0 && <div className="p-3 text-xs text-slate-400">This PO has no line items.</div>}
          {poLines.map((ln, i) => {
            const key = ln.boq_id || ln.material_id || ln.description;
            const sel = lines.find(l => l.key === key);
            return (
              <div key={i} className={`flex items-center gap-3 px-3 py-2 text-sm ${sel ? 'bg-indigo-50/60' : ''}`}>
                <input type="checkbox" checked={!!sel} onChange={() => toggleLine(ln)} data-testid={`vcr-line-${i}`} />
                <div className="flex-1 min-w-0 truncate">{ln.description || ln.material_name} <span className="text-slate-400 text-xs">· {ln.quantity} {ln.unit || ln.uom}</span></div>
                {sel && <Input type="number" min="0" step="any" value={sel.rejected_qty} onChange={e => setQty(key, e.target.value)} className="h-7 w-24" placeholder="Qty" data-testid={`vcr-line-qty-${i}`} />}
              </div>
            );
          })}
        </div>
      )}
      <Input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="What should the vendor do? (remarks)" data-testid="vcr-remarks" />
      <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={newInvoice} onChange={e => setNewInvoice(e.target.checked)} /> A corrected invoice is expected</label>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="button" disabled={saving} onClick={submit} className="bg-[#3b4872] hover:bg-[#2d3856]" data-testid="vcr-raise-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Raise request
        </Button>
      </div>
    </div>
  );
}

export default VendorChangeRequests;
